import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  icon?: IconDefinition;
  variant?: "default" | "danger";
  loading?: boolean;
  hidden?: boolean;
  disabled?: boolean;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  align?: "left" | "right";
}

const ActionMenu: React.FC<ActionMenuProps> = ({ items, align = "right" }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);

  const visibleItems = items.filter((item) => !item.hidden);

  // The menu is rendered fixed (out of the table's overflow context) so it is
  // never clipped. Position it under the trigger, flipping above/left as needed
  // to stay in the viewport.
  const positionMenu = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth || 160;
    const menuHeight = menuRef.current?.offsetHeight || 0;
    const gap = 4;

    let top = rect.bottom + gap;
    // Flip above the trigger if it would overflow the bottom of the viewport.
    if (menuHeight && top + menuHeight > window.innerHeight - 8) {
      top = rect.top - gap - menuHeight;
    }

    let left =
      align === "right" ? rect.right - menuWidth : rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));

    setMenuPos({ top, left });
  };

  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }

    // Position after the menu is in the DOM so we can measure it.
    positionMenu();

    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    const reposition = () => setOpen(false); // close on scroll/resize

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEsc);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEsc);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <div className="action-menu-wrapper" ref={wrapperRef}>
      <span
        ref={triggerRef}
        className="action-menu-trigger as-link"
        title="Actions"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </span>

      {open && (
        <ul
          ref={menuRef}
          className={`action-menu-list action-menu-fixed action-menu-${align}`}
          style={{
            position: "fixed",
            top: menuPos ? menuPos.top : -9999,
            left: menuPos ? menuPos.left : -9999,
            visibility: menuPos ? "visible" : "hidden",
          }}
        >
          {visibleItems.map((item, index) => (
            <li
              key={`action-menu-item-${index}`}
              className={`action-menu-item${
                item.variant === "danger" ? " is-danger" : ""
              }${item.disabled ? " is-disabled" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                if (item.disabled || item.loading) {
                  return;
                }
                setOpen(false);
                item.onClick();
              }}
            >
              {item.loading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                item.icon && <FontAwesomeIcon icon={item.icon} />
              )}
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ActionMenu;
